/** @jsx jsx */

import { Fragment as _ } from 'react';
import { css, jsx } from '@emotion/core';
import { useSelector } from 'react-redux';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import EditMarker from 'components/UI/EditMarker';
import HeaderStandard from 'components/Header';
import { HeaderGraphic } from './HeaderGraphic';
import { useHeader } from 'services/useHeader';
import { getIsFirstInteraction } from 'services/getIsFirstInteraction';
import getBlockById from 'services/apiHelpers/getBlockById';
import { Block } from 'factories/block';
import { State } from 'store/store';

const defaultHeaderHeight = 70;

const Header = (props: any) => {
  const { currentTopicIndex, topics } = props;

  const header = useHeader();
  const headerId = header?.block?.id;
  const { standard, graphic } = header;

  const { currentHeaderLogoBlock, currentHeader, headerStyle } = useSelector(
    (state: State) => {
      const currentHeaderBlock = getBlockById(header?.block?.id) as
        | Block
        | undefined;

      if (!currentHeaderBlock) {
        return {
          currentHeaderLogoBlock: undefined,
          currentHeader: standard,
          headerStyle: 'standard',
        };
      }

      const headerStyle = currentHeaderBlock?.props?.headerStyle || 'standard';

      const currentHeader = headerStyle === 'standard' ? standard : graphic;

      const currentHeaderLogoBlock = getBlockById(
        currentHeader?.logoBlock?.id
      ) as Block;

      return {
        currentHeaderLogoBlock,
        currentHeader,
        headerStyle,
      };
    }
  );

  const { currentInteraction, focusedNodeId, hideHeader } = useSelector(
    (state: any) => {
      return {
        currentInteraction: state.currentInteraction,
        focusedNodeId: state.focusedNodeId,
        hideHeader: state.hideHeader,
      } as any;
    }
  );

  const { isFixedHeaderHeight } = currentHeader?.props;

  // this check is for first FIRST run. where the header might not yet be ' ready '
  const selected = headerId ? focusedNodeId === headerId : false;

  const { headerHeight = defaultHeaderHeight } = currentHeader?.props;
  const editWrapperMargin = 3;

  const isFirstInteraction = getIsFirstInteraction(topics, currentInteraction);

  let height;

  if (!headerStyle || headerStyle === 'standard') {
    height = 60 + editWrapperMargin;
  } else {
    height =
      (!isFirstInteraction && !isFixedHeaderHeight
        ? defaultHeaderHeight
        : headerHeight) -
      editWrapperMargin * 2;
  }

  if (hideHeader) {
    return null;
  }

  return (
    <_>
      {headerStyle === 'standard' ? (
        // @ts-ignore
        <HeaderStandard
          currentTopicIndex={currentTopicIndex}
          topics={topics}
          headerProps={{
            ...standard.props,
            headerLogo: currentHeaderLogoBlock?.props?.src,
          }}
        />
      ) : (
        <HeaderGraphic
          key={`isFixedHeaderHeight-${isFixedHeaderHeight}`}
          currentTopicIndex={currentTopicIndex}
          topics={topics}
          headerProps={{
            ...graphic.props,
            headerLogo: currentHeaderLogoBlock?.props?.src,
          }}
          isFirstInteraction={isFirstInteraction}
        />
      )}

      {window.dev() && (
        <div
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            width: '100vw',
            height,
            // setting the index at 1 seems to keep it on top of
            // 2147483647 < max zIndex not needed
            zIndex: 1,
          }}
        >
          <div
            css={css({
              position: 'absolute',
              left: editWrapperMargin,
              top: editWrapperMargin,
              width: `calc(100vw - ${editWrapperMargin * 2}px)`,
              height: '100%',
              boxShadow: `0 0 0 1px ${
                selected ? '#ff0063' : 'rgba(0,0,0, 0.2)'
              }`,
              transition: 'box-shadow 0.2s',
              ...(!selected && {
                cursor: 'pointer',
                ':hover': {
                  boxShadow: `0 0 0 1px rgba(0,0,0,0.1)`,
                },
              }),
              zIndex: 1,
            })}
            onClick={() => {
              setCurrentBlock({ id: headerId });
            }}
          >
            {!selected && <EditMarker />}
          </div>
        </div>
      )}
    </_>
  );
};

export default Header;
